import { ICartEntry } from 'features/cart/types';
import { mapMenuSelectionsToICartEntry } from 'features/cart/utils/minimal-cart';
import { FavoriteCardData } from 'features/menu/pages/favorites-page/favorites-card-list/types';

import {
  IConfigOfferEntryDto,
  IMenuSelectionInput,
  IMinimalFavoriteFragment,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
} from 'generated/graphql-gateway';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';

export const mapCartEntriesToMenuSelections = (entries: ICartEntry[]): IMenuSelectionInput[] => {
  return entries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: mapCartEntriesToMenuSelections(entry.children ?? []),
  }));
};

type FavoriteEntryNode = {
  id: string;
  quantity: number;
  menuSelections?: ReadonlyArray<FavoriteEntryNode> | null;
};

const mapFavoriteEntryNodesToMenuSelections = (
  entries?: ReadonlyArray<FavoriteEntryNode> | null
): IMenuSelectionInput[] => {
  if (!entries?.length) {
    return [];
  }

  return entries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: mapFavoriteEntryNodesToMenuSelections(entry.menuSelections),
  }));
};

interface IMapMinimalFavoriteToCartEntriesOptions {
  favorite: IMinimalFavoriteFragment;
  menu: IndexedMenu<ILocalizedMenuEntry>;
  indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto>;
  indexedRewards: IndexedMenu<IRewardEntryDto>;
  posServiceMode: 'delivery' | 'pickup';
  url: string;
}

export const mapMinimalFavoriteToCartEntries = ({
  favorite,
  menu,
  indexedOffers,
  indexedRewards,
  posServiceMode,
  url,
}: IMapMinimalFavoriteToCartEntriesOptions): ICartEntry[] => {
  const minimalFavoriteEntries = mapFavoriteEntryNodesToMenuSelections(
    favorite.minimalFavoriteEntries as ReadonlyArray<FavoriteEntryNode>
  );

  return mapMenuSelectionsToICartEntry(minimalFavoriteEntries, {
    menu,
    indexedOffers,
    indexedRewards,
    posServiceMode,
    url,
  });
};

type MapMinimalFavoriteToCardDataArgs = Parameters<typeof mapMinimalFavoriteToCartEntries>[0];

export const mapMinimalFavoriteToCardData = (
  args: MapMinimalFavoriteToCardDataArgs
): FavoriteCardData => {
  const { favorite } = args;
  const minimalEntries = mapMinimalFavoriteToCartEntries(args);

  return {
    id: (favorite as IMinimalFavoriteFragment)?._id ?? '',
    name: (favorite as IMinimalFavoriteFragment)?.name ?? '',
    entries: [],
    minimalEntries,
  };
};

const mapMinimalChildrenToBackendEntries = (
  entries: ICartEntry[],
  parentLineId: string
): Array<Record<string, unknown>> => {
  return entries.map((entry, index) => {
    const lineId = entry.cartId ?? `${parentLineId}-${entry.id}-${index}`;
    return {
      lineId,
      sanityId: entry.id,
      quantity: entry.quantity,
      name: entry.name,
      image: JSON.stringify(entry.image ?? {}),
      pickerSelections: '{}',
      type: entry.type,
      url: entry.url,
      children: mapMinimalChildrenToBackendEntries(entry.children ?? [], lineId),
    };
  });
};

export const mapMinimalCartEntryToBackendEntry = (
  entry: ICartEntry,
  index: number
): Record<string, unknown> => {
  const lineId = entry.cartId ?? `${entry.id}-${index}`;

  return {
    lineId,
    sanityId: entry.id,
    quantity: entry.quantity,
    name: entry.name,
    image: JSON.stringify(entry.image ?? {}),
    pickerSelections: '{}',
    type: entry.type,
    url: entry.url,
    children: mapMinimalChildrenToBackendEntries(entry.children ?? [], lineId),
  };
};
